export interface RuntimeConfigEnv {
  VITE_ADMIN_API_BASE_URL?: string;
  VITE_GENERATION_API_BASE_URL?: string;
  VITE_ALLOW_LOCAL_API_IN_PRODUCTION?: string | boolean;
}

export interface RuntimeConfig {
  apiBaseUrl: string;
  allowLocalApiInProduction: boolean;
  isProduction: boolean;
}

export interface RuntimeConfigValidation {
  ok: boolean;
  errors: string[];
}

export const defaultApiBaseUrl = "https://api.pet.autumnsoft.net";
export const runtimeConfig = createRuntimeConfig(import.meta.env, import.meta.env.PROD);

export function createRuntimeConfig(env: RuntimeConfigEnv, isProduction: boolean): RuntimeConfig {
  const apiBaseUrl = normalizeBaseUrl(
    readString(env.VITE_ADMIN_API_BASE_URL) ||
    readString(env.VITE_GENERATION_API_BASE_URL) ||
    defaultApiBaseUrl
  );
  const allowLocalApiInProduction = readBoolean(env.VITE_ALLOW_LOCAL_API_IN_PRODUCTION, false);

  return {
    apiBaseUrl,
    allowLocalApiInProduction,
    isProduction
  };
}

export function validateRuntimeConfig(config: RuntimeConfig): RuntimeConfigValidation {
  const errors: string[] = [];
  if (config.isProduction && config.apiBaseUrl && !isAbsoluteHttpUrl(config.apiBaseUrl)) {
    errors.push("生产环境 API 地址必须是完整的 http(s) 地址。");
  }
  if (config.isProduction && isAbsoluteHttpUrl(config.apiBaseUrl) && !config.apiBaseUrl.startsWith("https://")) {
    errors.push("生产环境 API 地址必须使用 HTTPS。");
  }
  if (config.isProduction && !config.allowLocalApiInProduction && isLocalApiBase(config.apiBaseUrl)) {
    errors.push("生产环境 API 地址不能指向 localhost 或 127.0.0.1。");
  }
  return { ok: errors.length === 0, errors };
}

export function buildApiUrl(path: string) {
  return `${runtimeConfig.apiBaseUrl}${path}`;
}

function readString(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

function readBoolean(value: string | boolean | undefined, fallback: boolean) {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value !== "string" || !value.trim()) {
    return fallback;
  }
  const normalized = value.trim().toLowerCase();
  if (["true", "1", "yes", "on"].includes(normalized)) {
    return true;
  }
  if (["false", "0", "no", "off"].includes(normalized)) {
    return false;
  }
  return fallback;
}

function normalizeBaseUrl(value: string) {
  return value.replace(/\/+$/, "");
}

function isLocalApiBase(value: string) {
  try {
    const url = new URL(value);
    return ["localhost", "127.0.0.1", "::1"].includes(url.hostname);
  } catch {
    return false;
  }
}

function isAbsoluteHttpUrl(value: string) {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}
